import React, { lazy, Suspense, useEffect, useRef } from 'react';
import { useCookies } from 'react-cookie';
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation,
} from 'react-router-dom';
import Box from '@mui/material/Box';
import { styled } from '@mui/material/styles';
import { I18nextProvider } from 'react-i18next';
import i18n from './i18n';
import { useAnalytics } from './hooks/useAnalytics';

import Navbar from './components/Navbar/Navbar';
import AdminNavbar from './components/Navbar/AdminNavbar';
import ResellerNavbar from './components/Navbar/ResellerNavbar';
import PrivateRoute from './PrivateRoute';
import { Logout } from './services/Api/userService';
import ClientAccessIndicator from './components/ClientAccessIndicator/ClientAccessIndicator';
import OnboardingTourLauncher from './components/OnboardingTour/OnboardingTourLauncher';
import { NotificationsProvider } from './context/Notifications/NotificationsProvider';
import ErrorBoundary from './components/ErrorBoundary/ErrorBoundary';
import {
  GritClawProvider,
  useGritClaw,
} from './context/GritClaw/GritClawProvider';
import {
  COLORS,
  GRITCLAW_INSET_VAR,
  MOTION,
  REDUCED_MOTION,
} from './theme/tokens';
import { clearDashboardBootstrapCache } from './services/Api/apiDashboardBootstrap';

const Dashboard = lazy(() => import('./pages/Customer/Dashboard/Dashboard'));
const Locations = lazy(() => import('./pages/Customer/Locations/Locations'));
const ViewDataPage = lazy(() => import('./pages/Customer/RTL/ViewData'));
const DeviceDetailPage = lazy(
  () => import('./pages/Customer/Devices/DeviceDetailPage')
);
const DeviceWarnings = lazy(
  () => import('./pages/Customer/Devices/DeviceWarnings')
);
const RecentWarningsPage = lazy(
  () => import('./pages/Customer/RTL/RecentWarnings')
);
const RTLToleranceProfiles = lazy(
  () => import('./pages/Customer/RTL/RTLToleranceProfiles')
);
const Users = lazy(() => import('./pages/Customer/Users/Users'));
const Settings = lazy(() => import('./pages/Customer/Settings/Settings'));
const Support = lazy(() => import('./pages/Support/Support'));
const Login = lazy(() => import('./pages/Login/Login'));
const AdminDashboard = lazy(
  () => import('./pages/Admin/Dashboard/AdminDashboard')
);
const AdminClient = lazy(() => import('./pages/Admin/Client/AdminClient'));
const AdminReseller = lazy(
  () => import('./pages/Admin/Reseller/AdminReseller')
);
const AdminDevices = lazy(() => import('./pages/Admin/Devices/AdminDevices'));
const AdminGritClawUsage = lazy(
  () => import('./pages/Admin/GritClawUsage/AdminGritClawUsage')
);
const AdminOnomondo = lazy(
  () => import('./pages/Admin/Onomondo/AdminOnomondo')
);
const ResellerClient = lazy(
  () => import('./pages/Reseller/Client/ResellerClient')
);
const ResellerReseller = lazy(
  () => import('./pages/Reseller/Reseller/ResellerReseller')
);
const ResellerRtl = lazy(() => import('./pages/Reseller/Rtl/ResellerRtl'));
const NotFound = lazy(() => import('./pages/404/NotFound'));
const ForgotPassword = lazy(
  () => import('./pages/ForgotPassword/ForgotPassword')
);
const CreateNewPassword = lazy(
  () => import('./pages/CreateNewPassword/CreateNewPassword')
);
const CreateFirstPassword = lazy(
  () => import('./pages/CreateNewPassword/CreateFirstPassword')
);
const TermsAndConditions = lazy(
  () => import('./pages/TermsAndConditions/TermsAndConditions')
);
const AcceptTermsAndConditions = lazy(
  () => import('./pages/TermsAndConditions/AcceptT&C')
);
const ProfileSelection = lazy(
  () => import('./pages/ProfileSelection/ProfileSelection')
);
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy/PrivacyPolicy'));
const Rtls = lazy(() => import('./pages/Customer/RTL/Rtl'));
const Shipments = lazy(() => import('./pages/Customer/Shipments/Shipments'));
const ShipmentDetailPage = lazy(
  () => import('./pages/Customer/Shipments/Detail/ShipmentDetailPage')
);
const ReportsPage = lazy(() => import('./pages/Customer/Reports/Reports'));
const ReportBuilderPage = lazy(
  () => import('./pages/Customer/Reports/ReportBuilderPage')
);
const ReportViewPage = lazy(
  () => import('./pages/Customer/Reports/ReportViewPage')
);
const Devices = lazy(() => import('./pages/Customer/Devices/Devices'));
const Unsubscribe = lazy(() => import('./pages/Unsubscribe/Unsubscribe'));
const GritClawPage = lazy(() => import('./pages/GritClaw/GritClawPage'));
const SchedulesPage = lazy(() => import('./pages/GritClaw/SchedulesPage'));
const PreferencesPage = lazy(() => import('./pages/GritClaw/PreferencesPage'));
const AlertRulesPage = lazy(() => import('./pages/GritClaw/AlertRulesPage'));
const GritClawPanel = lazy(() =>
  import('./components/GritClaw/GritClawPanel').then((module) => ({
    default: module.GritClawPanel,
  }))
);
const CommandBar = lazy(() =>
  import('./components/GritClaw/CommandBar').then((module) => ({
    default: module.CommandBar,
  }))
);
const FullScreenOverlay = lazy(
  () => import('./components/GritClaw/FullScreenOverlay')
);

const BackgroundContainer = styled(Box)`
  display: flex;
  height: 100vh;
  overflow: hidden;
`;

interface CookieUser {
  email?: string;
  company_id?: string;
  accepted_terms?: boolean;
  super_user?: boolean;
  company_scope?: string;
  role?: string;
  [key: string]: unknown;
}

interface Cookies {
  user?: CookieUser;
  token?: string;
  super_user?: boolean;
  role?: string;
  original_company_id?: string;
  [key: string]: unknown;
}

// Mounts the GritClaw app-wide singletons (side panel, command bar, full-screen
// handoff overlay) only for an authenticated user on a real app page (mirrors
// NotificationWrapper's gating). The same route-exclusion list hides the panel
// and bar on the auth pages; the `/gritclaw` prefix is already in the list, so
// the command bar is also suppressed on the dedicated GritClaw screen where the
// composer is "at home". The FullScreenOverlay self-gates on `fullTransitioning`
// and is purely cosmetic, so it may render anywhere a transition is in flight.
//
// All three live here — a direct sibling of <Routes>, outside Layout's
// BackgroundContainer — so the command bar's `position: fixed` is anchored to
// the viewport (no CSS-transformed ancestor) and Layout's `overflow: hidden`
// cannot clip it. Everything stays inside GritClawProvider so useGritClaw works.
const GritClawWrapper: React.FC = () => {
  const [cookies] = useCookies([
    'user',
    'token',
    'super_user',
    'original_company_id',
  ]);
  const typedCookies = cookies as Cookies;
  const location = useLocation();
  const { fullTransitioning, setPanelOpen } = useGritClaw();
  // No assistant chrome during impersonation — the claw gateway's membership
  // revalidation rejects impersonation tokens (see gritClawClient).
  const impersonating =
    (!!typedCookies.super_user && !!typedCookies.user?.company_id) ||
    !!typedCookies.original_company_id;
  const blockedPrefixes = [
    '/gritclaw',
    '/login',
    '/forgot-password',
    '/reset-password',
    '/create-password',
    '/accept-t&c',
    '/profile-selection',
    '/privacy-policy',
    '/unsubscribe/',
  ];
  const blocked = blockedPrefixes.some((p) => location.pathname.startsWith(p));
  const showChrome =
    !!typedCookies.user && !!typedCookies.token && !blocked && !impersonating;
  // When the chrome is hidden (auth pages, the dedicated /gritclaw screen, logout)
  // the docked panel must not stay open — otherwise the content would keep its
  // reserved inset with no panel beside it. Collapsing it here also animates the
  // content back to full width as the user leaves.
  useEffect(() => {
    if (!showChrome) setPanelOpen(false);
  }, [showChrome, setPanelOpen]);
  return (
    <Suspense fallback={null}>
      {showChrome && <GritClawPanel />}
      {showChrome && <CommandBar />}
      {fullTransitioning && <FullScreenOverlay />}
    </Suspense>
  );
};

const App: React.FC = () => {
  return (
    <I18nextProvider i18n={i18n}>
      <ErrorBoundary>
        <NotificationsProvider>
          <Router>
            <GritClawProvider>
              <Suspense fallback={null}>
                <Routes>
                  <Route
                    path="/accept-t&c"
                    element={
                      <PrivateRoute element={AcceptTermsAndConditions} />
                    }
                  />
                  <Route
                    path="/profile-selection"
                    element={<ProfileSelection />}
                  />
                  <Route path="/*" element={<Layout />} />
                </Routes>
              </Suspense>
              <GritClawWrapper />
              <OnboardingTourLauncher />
            </GritClawProvider>
          </Router>
        </NotificationsProvider>
      </ErrorBoundary>
    </I18nextProvider>
  );
};

const Layout: React.FC = () => {
  const location = useLocation();
  const [cookies, , removeCookie] = useCookies([
    'user',
    'super_user',
    'token',
    'role',
    'settings',
    'original_company_id',
  ]);
  const typedCookies = cookies as Cookies;
  useAnalytics();
  const isAdmin = location.pathname.startsWith('/admin');
  const isReseller = location.pathname.startsWith('/reseller');

  const isLoginPage = location.pathname === '/login';
  const isForgotPassword = location.pathname === '/forgot-password';
  const isResetPassword = location.pathname === '/reset-password';
  const isCreatePassword = location.pathname === '/create-password';
  const isPrivacyPolicy = location.pathname === '/privacy-policy';
  const isSupportPage = location.pathname === '/support';
  const isTermsPage = location.pathname === '/termsandconditions';
  const isAcceptTermsPage = location.pathname === '/accept-t&c';
  const isUnsubscribePage = location.pathname.startsWith('/unsubscribe/');

  const isNotFoundPage =
    location.pathname === '*' ||
    (![
      '/',
      '/login',
      '/rtl',
      '/shipments',
      '/reports',
      '/devices',
      '/device-alerts',
      '/rtl-alerts',
      '/locations',
      '/users',
      '/gritclaw',
      '/gritclaw/schedules',
      '/gritclaw/preferences',
      '/gritclaw/alert-rules',
      '/settings',
      '/admin',
      '/termsandconditions',
      '/support',
      '/admin/clients',
      '/admin/rtl',
      '/admin/devices',
      '/admin/onomondo',
      '/reseller/clients',
      '/reseller/resellers',
      '/reseller/rtl',
      '/reseller/users',
      '/forgot-password',
    ].includes(location.pathname) &&
      !location.pathname.includes('/vehicles/') &&
      !location.pathname.includes('/seals/') &&
      !location.pathname.includes('/rtl/') &&
      !location.pathname.includes('/devices/') &&
      !location.pathname.includes('/shipments/') &&
      !location.pathname.includes('/reports/') &&
      !location.pathname.includes('/admin/') &&
      !location.pathname.includes('/reseller/') &&
      !location.pathname.startsWith('/unsubscribe/'));

  const prevCookiesRef = useRef<Cookies | null>(null);
  const prevDataContextRef = useRef<string | null>(null);
  const mainScrollRef = useRef<HTMLDivElement>(null);

  // The app scrolls inside this one container (not the window), so the
  // browser's native per-navigation scroll reset never fires — without this,
  // every page opens at the previous page's scroll offset.
  useEffect(() => {
    if (mainScrollRef.current) mainScrollRef.current.scrollTop = 0;
  }, [location.pathname]);

  useEffect(() => {
    const contextKey = `${typedCookies.token || ''}:${
      typedCookies.user?.company_id || ''
    }`;
    if (
      prevDataContextRef.current !== null &&
      prevDataContextRef.current !== contextKey
    ) {
      clearDashboardBootstrapCache();
    }
    prevDataContextRef.current = contextKey;
  }, [typedCookies.token, typedCookies.user?.company_id]);

  useEffect(() => {
    const prevCookies = prevCookiesRef.current;

    if (!prevCookies) {
      prevCookiesRef.current = typedCookies;
      return;
    }

    // Only check for user email changes (different user logged in)
    // Don't logout for token/role changes - those are normal
    if (
      typedCookies.token &&
      prevCookies.token &&
      prevCookies.user?.email &&
      typedCookies.user?.email &&
      prevCookies.user.email !== typedCookies.user.email
    ) {
      // Different user logged in - logout the previous user
      if (prevCookies.user?.email && prevCookies.token) {
        Logout({
          email: prevCookies.user.email,
          token: prevCookies.token,
        }).catch(() => {
          // Ignore logout errors
        });
      }
      removeCookie('user', { path: '/' });
      removeCookie('super_user', { path: '/' });
      removeCookie('original_company_id', { path: '/' });
      removeCookie('role', { path: '/' });
      removeCookie('token', { path: '/' });
      removeCookie('settings', { path: '/' });
    }

    prevCookiesRef.current = typedCookies;
  }, [typedCookies, removeCookie]);

  return (
    <BackgroundContainer
      sx={{
        backgroundColor: COLORS.surfaceApp,
      }}
    >
      {!isLoginPage &&
        !isNotFoundPage &&
        !isForgotPassword &&
        !isAdmin &&
        !isReseller &&
        !isPrivacyPolicy &&
        !isSupportPage &&
        !isTermsPage &&
        !isResetPassword &&
        !isCreatePassword &&
        !isAcceptTermsPage &&
        !isUnsubscribePage && <Navbar />}

      {!isLoginPage &&
        !isNotFoundPage &&
        !isForgotPassword &&
        !isPrivacyPolicy &&
        !isSupportPage &&
        !isTermsPage &&
        isAdmin &&
        !isResetPassword &&
        !isCreatePassword &&
        !isAcceptTermsPage &&
        !isUnsubscribePage && <AdminNavbar />}

      {!isLoginPage &&
        !isNotFoundPage &&
        !isForgotPassword &&
        !isPrivacyPolicy &&
        !isSupportPage &&
        !isTermsPage &&
        isReseller &&
        !isResetPassword &&
        !isCreatePassword &&
        !isAcceptTermsPage &&
        !isUnsubscribePage && <ResellerNavbar />}

      {/* Show AdminNavbar for super users on support/terms pages */}
      {typedCookies.super_user &&
        (isSupportPage || isTermsPage) &&
        !isLoginPage &&
        !isNotFoundPage && <AdminNavbar />}

      {/* Show ResellerNavbar for reseller users on support/terms pages */}
      {!typedCookies.super_user &&
        typedCookies.user?.company_scope === 'RESELLER' &&
        (isSupportPage || isTermsPage) &&
        !isLoginPage &&
        !isNotFoundPage && <ResellerNavbar />}

      {/* Show Navbar for regular users on support/terms pages */}
      {!typedCookies.super_user &&
        typedCookies.user?.company_scope !== 'RESELLER' &&
        (isSupportPage || isTermsPage) &&
        !isLoginPage &&
        !isNotFoundPage && <Navbar />}
      {!isAdmin &&
        !isSupportPage &&
        !isTermsPage &&
        (typedCookies.super_user || typedCookies.original_company_id) && (
          <ClientAccessIndicator />
        )}
      <Box
        component="main"
        flex={1}
        sx={(theme) => ({
          minWidth: 0,
          minHeight: 0,
          // Reserve a column for the docked GritClaw panel: when it opens, the
          // content shrinks and slides beside it (the panel fills the second
          // grid track) instead of being overlaid. Driven by the single CSS var
          // the provider publishes, so the slide stays in lockstep with the
          // drawer — and animating the grid track (not `margin`) keeps the
          // slide off the layout-property blacklist.
          display: 'grid',
          gridTemplateColumns: `minmax(0, 1fr) var(${GRITCLAW_INSET_VAR}, 0px)`,
          transition: `grid-template-columns ${MOTION.durationMedium}ms ${MOTION.easeOut}`,
          // At tablet / compact-laptop widths GritClaw is a temporary overlay,
          // so the operational page keeps its full track instead of also paying
          // the provider's desktop-only 420px dock inset.
          [theme.breakpoints.down('lg')]: {
            gridTemplateColumns: 'minmax(0, 1fr) 0px',
          },
          [REDUCED_MOTION]: { transition: 'none' },
        })}
      >
        <Box
          ref={mainScrollRef}
          sx={{
            minWidth: 0,
            minHeight: 0,
            overflowY: 'auto',
            display: 'flex',
            flexDirection: 'column',
            // The content area fills the full height; the app-wide GritClaw
            // command bar (fixed, bottom-centre, ~44px at rest) floats over it
            // the same way it does on the map dashboard. Scrollable content
            // (e.g. tables) keeps its own small bottom clearance so its last
            // rows sit above the bar, so no page-level bottom padding is
            // reserved here.
            pb: 0,
          }}
        >
          <Suspense fallback={null}>
            <Routes>
              <Route path="/" element={<PrivateRoute element={Dashboard} />} />
              <Route path="/login" element={<Login />} />
              <Route path="/rtl" element={<PrivateRoute element={Rtls} />} />
              <Route
                path="/rtl-alerts"
                element={<Navigate to="/device-alerts" replace />}
              />
              <Route
                path="/shipments"
                element={<PrivateRoute element={Shipments} />}
              />
              <Route
                path="/shipments/:id"
                element={<PrivateRoute element={ShipmentDetailPage} />}
              />
              <Route
                path="/reports"
                element={<PrivateRoute element={ReportsPage} />}
              />
              <Route
                path="/reports/new"
                element={<PrivateRoute element={ReportBuilderPage} />}
              />
              <Route
                path="/reports/:id"
                element={<PrivateRoute element={ReportViewPage} />}
              />
              <Route
                path="/reports/:id/edit"
                element={<PrivateRoute element={ReportBuilderPage} />}
              />
              <Route
                path="/devices"
                element={<PrivateRoute element={Devices} />}
              />
              <Route
                path="/devices/:id"
                element={<PrivateRoute element={DeviceDetailPage} />}
              />
              <Route
                path="/device-alerts"
                element={<PrivateRoute element={DeviceWarnings} />}
              />
              <Route
                path="/locations"
                element={<PrivateRoute element={Locations} />}
              />
              <Route
                path="/rtl/recent-warnings/:id"
                element={<PrivateRoute element={RecentWarningsPage} />}
              />
              <Route
                path="/rtl/profile/:id"
                element={<PrivateRoute element={RTLToleranceProfiles} />}
              />
              <Route
                path="/rtl/:id"
                element={<PrivateRoute element={ViewDataPage} />}
              />
              <Route
                path="/rtl/tolerance-Profiles"
                element={<PrivateRoute element={RTLToleranceProfiles} />}
              />
              <Route path="/users" element={<PrivateRoute element={Users} />} />
              <Route
                path="/gritclaw"
                element={<PrivateRoute element={GritClawPage} />}
              />
              <Route
                path="/gritclaw/schedules"
                element={<PrivateRoute element={SchedulesPage} />}
              />
              <Route
                path="/gritclaw/preferences"
                element={<PrivateRoute element={PreferencesPage} />}
              />
              <Route
                path="/gritclaw/alert-rules"
                element={<PrivateRoute element={AlertRulesPage} />}
              />
              <Route
                path="/settings"
                element={<PrivateRoute element={Settings} excludeReadOnly />}
              />
              {/* The navbar is picked by URL prefix, so admin/reseller need
                  their own Settings paths — sending them to /settings swapped
                  them into the customer shell and out of their portal. */}
              <Route
                path="/admin/settings"
                element={<PrivateRoute element={Settings} adminOnly />}
              />
              <Route
                path="/reseller/settings"
                element={<PrivateRoute element={Settings} resellerOnly />}
              />
              <Route
                path="/admin"
                element={<PrivateRoute element={AdminDashboard} adminOnly />}
              />
              <Route
                path="/termsandconditions"
                element={<TermsAndConditions />}
              />
              <Route path="/support" element={<Support />} />
              <Route
                path="/admin/clients"
                element={<PrivateRoute element={AdminClient} adminOnly />}
              />
              <Route
                path="/admin/resellers"
                element={<PrivateRoute element={AdminReseller} adminOnly />}
              />
              <Route
                path="/admin/devices"
                element={<PrivateRoute element={AdminDevices} adminOnly />}
              />
              <Route
                path="/admin/gritclaw-usage"
                element={
                  <PrivateRoute element={AdminGritClawUsage} adminOnly />
                }
              />
              <Route
                path="/admin/onomondo"
                element={<PrivateRoute element={AdminOnomondo} adminOnly />}
              />
              {/* RTLs are managed under the unified Devices page. Old links
                  and bookmarks land there instead of a parallel RTL list. */}
              <Route
                path="/admin/rtl"
                element={<Navigate to="/admin/devices" replace />}
              />
              <Route
                path="/admin/rtl/:id"
                element={<PrivateRoute element={ViewDataPage} adminOnly />}
              />
              <Route
                path="/admin/rtl/warnings/:id"
                element={
                  <PrivateRoute element={RecentWarningsPage} adminOnly />
                }
              />
              <Route
                path="/admin/rtl/profiles/:id"
                element={
                  <PrivateRoute element={RTLToleranceProfiles} adminOnly />
                }
              />
              <Route
                path="/reseller/clients"
                element={<PrivateRoute element={ResellerClient} resellerOnly />}
              />
              <Route
                path="/reseller/resellers"
                element={
                  <PrivateRoute element={ResellerReseller} resellerOnly />
                }
              />
              <Route
                path="/reseller/rtl"
                element={<PrivateRoute element={ResellerRtl} resellerOnly />}
              />
              <Route
                path="/reseller/users"
                element={<PrivateRoute element={Users} resellerOnly />}
              />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password" element={<CreateNewPassword />} />
              <Route
                path="/create-password"
                element={<CreateFirstPassword />}
              />
              <Route path="/privacy-policy" element={<PrivacyPolicy />} />
              <Route path="/support" element={<Support />} />
              <Route path="/unsubscribe/:token" element={<Unsubscribe />} />
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
        </Box>
      </Box>
    </BackgroundContainer>
  );
};

export default App;
